import React, { useState } from 'react';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Table, Button, Text } from '@shared/components/bootstrap';

import { Panel } from '@admin/components/helpers/panel';
import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { HRS_CONFIG } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { Hrs__ProfileDefault, useHrsProfileDefaultsQuery } from '@admin/schema';

import { UpsertModal } from './upsert_modal';

export const HRSProfileDefaultsList: React.FC = () => {
  useTitle('HRS Profile Defaults');
  const [showUpsertModal, setShowUpsertModal] = useState<boolean>(false);
  const [editingProfileDefault, setEditingProfileDefault] = useState<Hrs__ProfileDefault | undefined>(undefined);
  const canView = usePermission(HRS_CONFIG.resource, HRS_CONFIG.actions.read);
  const canUpdate = usePermission(HRS_CONFIG.resource, HRS_CONFIG.actions.update);

  const { data, loading, refetch } = useHrsProfileDefaultsQuery({
    client,
    skip: !canView,
  });

  return (
    <Permission
      resource={HRS_CONFIG.resource}
      action={HRS_CONFIG.actions.read}
      denied={
        <Panel>
          <Panel.Body>
            <Text tag="p" style="muted">
              You do not have permission to view HRS configuration.
            </Text>
          </Panel.Body>
        </Panel>
      }
    >
      <Panel>
        <Panel.Body>
          <Table responsive striped>
            <thead>
              <tr>
                <th className="col-sm-2">Job Code & Title</th>
                <th className="col-sm-2">Location</th>
                <th className="col-sm-2">Org Level 1</th>
                <th className="col-sm-2">Org Level 2</th>
                <th className="col-sm-2">Org Level 3</th>
                <th className="col-sm-2">Org Level 4</th>
                <th className="col-sm-2">Team</th>
                {canUpdate && (
                  <th className="col-sm-1 text-right">
                    <Button
                      type="button"
                      kind="mint"
                      onClick={() => {
                        setEditingProfileDefault(undefined);
                        setShowUpsertModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add
                    </Button>
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {data?.hrsProfileDefaults.map((profileDefault) => (
                <tr key={profileDefault.id}>
                  <td className="col-sm-2">{profileDefault.businessTitle}</td>
                  <td className="col-sm-2">{profileDefault.location?.code ?? 'Any'}</td>
                  <td className="col-sm-2">{profileDefault.orgLevel1}</td>
                  <td className="col-sm-2">{profileDefault.orgLevel2}</td>
                  <td className="col-sm-2">{profileDefault.orgLevel3}</td>
                  <td className="col-sm-2">{profileDefault.orgLevel4}</td>
                  <td className="col-sm-2">{profileDefault.team?.name}</td>
                  {canUpdate && (
                    <td className="col-sm-1 text-right">
                      <Button
                        kind="primary"
                        onClick={() => {
                          setEditingProfileDefault(profileDefault);
                          setShowUpsertModal(true);
                        }}
                      >
                        Edit
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
          {loading && <Spinner />}
        </Panel.Body>
      </Panel>
      {showUpsertModal && canUpdate && (
        <UpsertModal
          currentBusinessTitles={data?.hrsBusinessTitles ?? []}
          locationOptions={data?.hrsLocations ?? []}
          profileDefault={editingProfileDefault}
          teamOptions={data?.teams ?? []}
          closeModal={() => {
            setEditingProfileDefault(undefined);
            setShowUpsertModal(false);
            refetch();
          }}
        />
      )}
    </Permission>
  );
};
